import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { LogOut } from "lucide-react";
import { Logo, StatusPill } from "@/components/brand";
import NotificationBell from "@/components/NotificationBell";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";

const STEP_LABELS = { pending: "Received", under_review: "Under review", approved: "Approved", rejected: "Decision" };

export default function ApplicationStatus() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [appData, setAppData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/applications/me");
        setAppData(data);
      } catch {}
      setLoading(false);
    })();
  }, []);

  const doLogout = async () => { await logout(); navigate("/"); };
  const status = appData?.status || "pending";
  const steps = ["pending", "under_review", status === "rejected" ? "rejected" : "approved"];
  const currentIdx = status === "rejected" ? 2 : status === "approved" ? 2 : status === "under_review" ? 1 : 0;

  return (
    <div className="min-h-screen bg-white">
      <header className="h-16 border-b border-gray-200 flex items-center justify-between px-6 lg:px-10">
        <Logo />
        <div className="flex items-center gap-4">
          <NotificationBell />
          <button onClick={doLogout} data-testid="logout-button" className="flex items-center gap-2 text-sm text-gray-600 hover:text-orange-600">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </header>

      <div className="mx-auto max-w-3xl px-6 py-14" data-testid="application-status">
        <div className="flex items-center gap-4 mb-6">
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">[01] Application Status</span>
          <span className="h-px flex-1 bg-gray-200" />
          <span className="font-mono text-[11px] tracking-[0.18em] uppercase text-gray-400">Driver Review</span>
        </div>

        <div className="flex items-center gap-4 mb-3">
          <h1 className="headline text-4xl text-[#0a0a0a]">Hi, {user?.name?.split(" ")[0]}.</h1>
          {!loading && <StatusPill status={status} />}
        </div>
        <p className="text-base text-gray-500">
          {status === "approved"
            ? "You're approved. Reloading your dashboard…"
            : status === "rejected"
            ? "Your application was not approved at this time."
            : "Your application is with our operations team. We review every applicant and will email you within 48 hours."}
        </p>

        {/* Progress */}
        <div className="mt-10 flex items-center">
          {steps.map((s, i) => (
            <div key={s} className="flex-1 flex items-center">
              <div className="flex flex-col items-center">
                <div className={`h-3 w-3 rounded-full ${i <= currentIdx ? (s === "rejected" ? "bg-orange-500" : "bg-[#0a0a0a]") : "bg-gray-200"}`} />
                <span className="mt-2 font-mono text-[10px] tracking-[0.12em] uppercase text-gray-500">{STEP_LABELS[s]}</span>
              </div>
              {i < steps.length - 1 && <div className={`h-px flex-1 mx-2 ${i < currentIdx ? "bg-[#0a0a0a]" : "bg-gray-200"}`} />}
            </div>
          ))}
        </div>

        {status === "rejected" && appData?.reject_reason && (
          <div className="mt-8 border border-orange-500 bg-orange-50 p-5">
            <div className="font-mono text-[11px] tracking-[0.14em] uppercase text-orange-700 mb-2">Reason</div>
            <p className="text-sm text-orange-800">{appData.reject_reason}</p>
          </div>
        )}

        {appData && (
          <div className="mt-10 border border-gray-200 divide-y divide-gray-200">
            {[
              ["Region", appData.region],
              ["Licence", `${appData.licence_class} · ${appData.licence_number}`],
              ["Vehicle", `${appData.vehicle?.year} ${appData.vehicle?.make} ${appData.vehicle?.model}`],
              ["Insurance", appData.insurance?.provider],
              ["Submitted", new Date(appData.created_at).toLocaleString()],
            ].map(([k, v]) => (
              <div key={k} className="flex items-center justify-between px-5 py-3.5">
                <span className="font-mono text-[11px] tracking-[0.14em] uppercase text-gray-400">{k}</span>
                <span className="text-sm text-[#0a0a0a]">{v}</span>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
